'use client'

// Favourites on Supabase (2026-08-26): the Clerk-era version fetched via Payload
// server actions and had been a permanent no-op since the static cutover (the auth
// shim reports everyone signed out, and the actions hit the retired Mongo). The
// context now keys favourites by CONTENT PATH (not CMS document id) and reads
// user_preferences kind='bookmark' through the browser Supabase client.
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useSupabaseAuth } from '@/context/supabaseContext'
import { listBookmarks } from '@/lib/supabase/engagement'

type UserFavouritesContextType = {
  /** Content paths (e.g. /portfolio/andritz) the member has favourited. */
  userFavouriteIds: string[]
  isLoading: boolean
  refetch: () => void
  addFavourite: (path: string) => void
  removeFavourite: (path: string) => void
}

const UserFavouritesContext = createContext<UserFavouritesContextType>({
  userFavouriteIds: [],
  isLoading: false,
  refetch: () => {},
  addFavourite: () => {},
  removeFavourite: () => {},
})

export const UserFavouritesProvider = ({ children }: { children: ReactNode }) => {
  const { user, isLoaded } = useSupabaseAuth()
  const [userFavouriteIds, setUserFavouriteIds] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const fetchFavourites = async () => {
    if (typeof window === 'undefined') return
    if (!isLoaded || !user) {
      setUserFavouriteIds([])
      setIsLoading(false)
      return
    }
    try {
      setIsLoading(true)
      const rows = await listBookmarks()
      setUserFavouriteIds(rows.map((r) => r.content_path))
    } catch (err) {
      console.error('Error fetching user favourites:', err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      fetchFavourites()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isLoaded])

  const refetch = () => {
    fetchFavourites()
  }

  const addFavourite = (path: string) => {
    setUserFavouriteIds((prev) => (prev.includes(path) ? prev : [...prev, path]))
  }

  const removeFavourite = (path: string) => {
    setUserFavouriteIds((prev) => prev.filter((p) => p !== path))
  }

  return (
    <UserFavouritesContext.Provider
      value={{ userFavouriteIds, isLoading, refetch, addFavourite, removeFavourite }}
    >
      {children}
    </UserFavouritesContext.Provider>
  )
}

export const useUserFavourites = () => useContext(UserFavouritesContext)
